"use client";

import { useState, useMemo, useCallback, useEffect } from "react";
import {
  loadDashboard,
  loadCostAnalysis,
  loadExpenses,
  loadIncome,
  loadInitiatives,
  loadPeriod,
  prefillIncome,
  saveIncome,
  saveInitiative,
  savePeriod,
  generateCostAnalysis,
  updateIncome,
  updateInitiative,
  deleteIncome,
  fromApi,
} from "./roiApi";
import {
  n,
  keyFromName,
  incomeTotals,
  expenseTotals,
  displayMultiplier,
} from "./utils";

const emptyIncomeRow = (customIncomeColumns, data = {}) => {
  const customIncome = {};
  customIncomeColumns.forEach((col) => {
    customIncome[col.key] = n(data.customIncome?.[col.key]);
  });

  return {
    className: data.className || "",
    section: data.section || "",
    totalStudents: data.totalStudents || 0,
    ewsStudents: data.ewsStudents || 0,
    ewsPayPercent: data.ewsPayPercent ?? 0,
    waiverStudents: data.waiverStudents || 0,
    waiverPercent: data.waiverPercent ?? 50,
    tuitionIncome: data.tuitionIncome || 0,
    admissionIncome: data.admissionIncome || 0,
    developmentIncome: data.developmentIncome || 0,
    transportIncome: data.transportIncome || 0,
    examIncome: data.examIncome || 0,
    activityIncome: data.activityIncome || 0,
    otherIncome: data.otherIncome || 0,
    annualIncome: data.annualIncome || 0,
    customIncome,
    collectionPercent: data.collectionPercent ?? 90,
    amountCollected: data.amountCollected || 0,
    pendingDue: data.pendingDue || 0,
  };
};

const initiativePayload = (state) => ({
  name: state.roiInvestmentName?.trim() || "ROI initiative",
  one_time_investment: n(state.roiOneTimeInvestment),
  monthly_investment: n(state.roiMonthlyInvestment),
  period_months: Math.max(n(state.roiPeriodMonths), 1),
  other_monthly_gain: n(state.roiOtherMonthlyGain),
  monthly_saving: n(state.roiMonthlySaving),
});

export function useDashboardState() {
  const [incomeRows, setIncomeRows] = useState([]);
  const [expenseRows, setExpenseRows] = useState([]);
  const [customIncomeColumns, setCustomIncomeColumns] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");
  const [month, setMonth] = useState("April");
  const [viewMode, setViewMode] = useState("monthly");
  const [annualMode, setAnnualModeState] = useState("spread");
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saveMessage, setSaveMessage] = useState("");
  const [incomeSaving, setIncomeSaving] = useState(false);
  const [roiSaving, setRoiSaving] = useState(false);
  const [roiInitiativeId, setRoiInitiativeId] = useState(null);
  const [costAnalysis, setCostAnalysis] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisGenerating, setAnalysisGenerating] = useState(false);

  const [roiInvestmentName, setRoiInvestmentName] = useState("");
  const [roiOneTimeInvestment, setRoiOneTimeInvestment] = useState(0);
  const [roiMonthlyInvestment, setRoiMonthlyInvestment] = useState(0);
  const [roiPeriodMonths, setRoiPeriodMonths] = useState(12);
  const [roiOtherMonthlyGain, setRoiOtherMonthlyGain] = useState(0);
  const [roiMonthlySaving, setRoiMonthlySaving] = useState(0);

  const refreshDashboard = useCallback(async () => {
    const overview = await loadDashboard(month, viewMode);
    setDashboard(overview);
    return overview;
  }, [month, viewMode]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    setSaveMessage("");

    Promise.all([
      loadDashboard(month, viewMode),
      loadPeriod(month),
      loadIncome(month),
      loadExpenses(month),
      loadInitiatives(month),
    ])
      .then(([overview, period, rows, expenses, initiatives]) => {
        if (!active) return;
        const normalizedRows = rows.map(fromApi);
        const columnKeys = new Set();
        normalizedRows.forEach((row) => {
          Object.keys(row.customIncome || {}).forEach((key) => columnKeys.add(key));
        });

        setDashboard(overview);
        setAnnualModeState(period.annualMode || "spread");
        setIncomeRows(normalizedRows);
        setExpenseRows(expenses.map(fromApi));
        setCustomIncomeColumns(
          Array.from(columnKeys).map((key) => ({
            key,
            name: key
              .split("_")
              .filter(Boolean)
              .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
              .join(" "),
          }))
        );

        const initiative = initiatives[0] ? fromApi(initiatives[0]) : null;
        setRoiInitiativeId(initiative?.id || null);
        setRoiInvestmentName(initiative?.name || "");
        setRoiOneTimeInvestment(initiative?.oneTimeInvestment || 0);
        setRoiMonthlyInvestment(initiative?.monthlyInvestment || 0);
        setRoiPeriodMonths(initiative?.periodMonths || 12);
        setRoiOtherMonthlyGain(initiative?.otherMonthlyGain || 0);
        setRoiMonthlySaving(initiative?.monthlySaving || 0);
      })
      .catch((err) => active && setError(err.message))
      .finally(() => active && setLoading(false));

    return () => {
      active = false;
    };
  }, [month, viewMode]);

  const income = useMemo(
    () => incomeTotals(incomeRows, customIncomeColumns, annualMode),
    [incomeRows, customIncomeColumns, annualMode]
  );
  const expense = useMemo(() => expenseTotals(expenseRows), [expenseRows]);
  const multiplier = useMemo(() => displayMultiplier(viewMode), [viewMode]);

  const setAnnualMode = useCallback(
    async (value) => {
      setAnnualModeState(value);
      setSaveMessage("");
      try {
        await savePeriod(month, value);
        await refreshDashboard();
      } catch (err) {
        setError(err.message);
      }
    },
    [month, refreshDashboard]
  );

  const addIncomeRow = useCallback(
    async (data = {}) => {
      setIncomeSaving(true);
      setSaveMessage("");
      try {
        const created = fromApi(await saveIncome(month, emptyIncomeRow(customIncomeColumns, data)));
        setIncomeRows((prev) => [...prev, created]);
        await refreshDashboard();
        setSaveMessage("Income row saved.");
      } catch (err) {
        setError(err.message);
      } finally {
        setIncomeSaving(false);
      }
    },
    [customIncomeColumns, month, refreshDashboard]
  );

  const updateIncomeRow = useCallback((id, key, value) => {
    setIncomeRows((prev) =>
      prev.map((row) =>
        row.id === id
          ? { ...row, [key]: key === "className" || key === "section" ? value : n(value) }
          : row
      )
    );
  }, []);

  const updateCustomIncome = useCallback((id, key, value) => {
    setIncomeRows((prev) =>
      prev.map((row) =>
        row.id === id
          ? { ...row, customIncome: { ...row.customIncome, [key]: n(value) } }
          : row
      )
    );
  }, []);

  const persistIncomeRow = useCallback(
    async (id) => {
      const row = incomeRows.find((item) => item.id === id);
      if (!row) return;
      setIncomeSaving(true);
      setSaveMessage("");
      try {
        const saved = fromApi(await updateIncome(month, id, row));
        setIncomeRows((prev) => prev.map((item) => (item.id === id ? saved : item)));
        await refreshDashboard();
        setSaveMessage("Income changes saved.");
      } catch (err) {
        setError(err.message);
      } finally {
        setIncomeSaving(false);
      }
    },
    [incomeRows, month, refreshDashboard]
  );

  const removeIncomeRow = useCallback(
    async (id) => {
      setIncomeSaving(true);
      setSaveMessage("");
      try {
        await deleteIncome(month, id);
        setIncomeRows((prev) => prev.filter((row) => row.id !== id));
        await refreshDashboard();
        setSaveMessage("Income row removed.");
      } catch (err) {
        setError(err.message);
      } finally {
        setIncomeSaving(false);
      }
    },
    [month, refreshDashboard]
  );

  const prefillIncomeRows = useCallback(async () => {
    setIncomeSaving(true);
    setSaveMessage("");
    try {
      const result = await prefillIncome(month);
      setIncomeRows(result.rows.map(fromApi));
      await refreshDashboard();
      setSaveMessage(`Prefilled ${result.created} class${result.created === 1 ? "" : "es"} and updated ${result.updated} existing row${result.updated === 1 ? "" : "s"}.`);
    } catch (err) {
      setError(err.message);
    } finally {
      setIncomeSaving(false);
    }
  }, [month, refreshDashboard]);

  const loadStoredCostAnalysis = useCallback(async () => {
    setAnalysisLoading(true);
    try {
      const result = await loadCostAnalysis(month, viewMode);
      setCostAnalysis(result.analysis || null);
    } catch (err) {
      setError(err.message);
    } finally {
      setAnalysisLoading(false);
    }
  }, [month, viewMode]);

  const generateStoredCostAnalysis = useCallback(async () => {
    setAnalysisGenerating(true);
    setError("");
    try {
      const result = await generateCostAnalysis(month, viewMode);
      setCostAnalysis(result);
      setSaveMessage("AI cost analysis generated.");
    } catch (err) {
      setError(err.message);
    } finally {
      setAnalysisGenerating(false);
    }
  }, [month, viewMode]);

  const addCustomIncomeColumn = useCallback(
    (name) => {
      const key = keyFromName(name);
      if (!key) return false;
      if (customIncomeColumns.some((col) => col.key === key)) return false;
      const newCol = { key, name };
      setCustomIncomeColumns((prev) => [...prev, newCol]);
      setIncomeRows((prev) =>
        prev.map((row) => ({
          ...row,
          customIncome: { ...row.customIncome, [key]: 0 },
        }))
      );
      setSaveMessage("New column added. Save each affected row after entering values.");
      return true;
    },
    [customIncomeColumns]
  );

  const removeCustomIncomeColumn = useCallback((key) => {
    setCustomIncomeColumns((prev) => prev.filter((col) => col.key !== key));
    setIncomeRows((prev) =>
      prev.map((row) => {
        const { [key]: _, ...rest } = row.customIncome || {};
        return { ...row, customIncome: rest };
      })
    );
    setSaveMessage("Column removed locally. Save affected rows to persist the change.");
  }, []);

  const addExpenseRow = useCallback((data = {}) => {
    const row = {
      id: `local-${Date.now()}`,
      category: data.category || "Salary & Staff Cost",
      head: data.head || "Teacher Salaries",
      includes: data.includes || "",
      amount: data.amount || 0,
      frequency: data.frequency || "monthly",
      type: data.type || "Fixed",
      priority: data.priority || "Essential",
    };
    setExpenseRows((prev) => [...prev, row]);
  }, []);

  const updateExpenseRow = useCallback((id, key, value) => {
    setExpenseRows((prev) =>
      prev.map((row) =>
        row.id === id ? { ...row, [key]: key === "amount" ? n(value) : value } : row
      )
    );
  }, []);

  const removeExpenseRow = useCallback((id) => {
    setExpenseRows((prev) => prev.filter((row) => row.id !== id));
  }, []);

  const saveRoiInitiative = useCallback(async () => {
    const currentState = {
      roiInvestmentName,
      roiOneTimeInvestment,
      roiMonthlyInvestment,
      roiPeriodMonths,
      roiOtherMonthlyGain,
      roiMonthlySaving,
    };
    setRoiSaving(true);
    setSaveMessage("");
    try {
      const payload = initiativePayload(currentState);
      const saved = fromApi(
        roiInitiativeId
          ? await updateInitiative(month, roiInitiativeId, payload)
          : await saveInitiative(month, payload)
      );
      setRoiInitiativeId(saved.id);
      setRoiInvestmentName(saved.name || "");
      setRoiOneTimeInvestment(saved.oneTimeInvestment || 0);
      setRoiMonthlyInvestment(saved.monthlyInvestment || 0);
      setRoiPeriodMonths(saved.periodMonths || 12);
      setRoiOtherMonthlyGain(saved.otherMonthlyGain || 0);
      setRoiMonthlySaving(saved.monthlySaving || 0);
      await refreshDashboard();
      setSaveMessage("ROI initiative saved.");
    } catch (err) {
      setError(err.message);
    } finally {
      setRoiSaving(false);
    }
  }, [
    month,
    refreshDashboard,
    roiInitiativeId,
    roiInvestmentName,
    roiOneTimeInvestment,
    roiMonthlyInvestment,
    roiPeriodMonths,
    roiOtherMonthlyGain,
    roiMonthlySaving,
  ]);

  return {
    incomeRows,
    expenseRows,
    customIncomeColumns,
    activeTab,
    month,
    viewMode,
    annualMode,
    roiInvestmentName,
    roiOneTimeInvestment,
    roiMonthlyInvestment,
    roiPeriodMonths,
    roiOtherMonthlyGain,
    roiMonthlySaving,
    income,
    expense,
    multiplier,
    dashboard,
    loading,
    error,
    saveMessage,
    incomeSaving,
    roiSaving,
    costAnalysis,
    analysisLoading,
    analysisGenerating,
    setActiveTab,
    setMonth,
    setViewMode,
    setAnnualMode,
    setRoiInvestmentName,
    setRoiOneTimeInvestment,
    setRoiMonthlyInvestment,
    setRoiPeriodMonths,
    setRoiOtherMonthlyGain,
    setRoiMonthlySaving,
    addIncomeRow,
    updateIncomeRow,
    updateCustomIncome,
    persistIncomeRow,
    prefillIncomeRows,
    removeIncomeRow,
    addCustomIncomeColumn,
    removeCustomIncomeColumn,
    addExpenseRow,
    updateExpenseRow,
    removeExpenseRow,
    saveRoiInitiative,
    loadStoredCostAnalysis,
    generateStoredCostAnalysis,
  };
}
